/* ==========================================================================
   Custom Curtain Measurement Tool
   Vanilla ES6, no dependencies.
   Works standalone OR embedded in a Shopify section.
   ========================================================================== */
(function () {
  'use strict';

  /* ----- Configuration & constants ----- */
  const STORAGE_KEY = 'curtarra.measurement.state.v1';
  const SHOP_SELECTION_STORAGE_KEY = 'curtarra.measurement.shopSelection.v1';
  const STATE_VERSION = 1;
  const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

  const HEADER_STYLES = [
    { value: 'grommet',             title: 'Grommet Top',         desc: 'A popular choice for contemporary spaces.' },
    { value: 'rod-pocket',          title: 'Rod Pocket',          desc: 'Lined on top for a clean, tailored look.' },
    { value: 'pinch-pleat-double',  title: 'Pinch Pleat — Double',desc: 'Creates a formal and elegant feel.' },
    { value: 'pinch-pleat-triple',  title: 'Pinch Pleat — Triple',desc: 'Formal, elegant and luxurious.' },
    { value: 'back-tab',            title: 'Back Tab',            desc: 'Soft and crisp, with hidden tabs.' },
    { value: 'tab-top',             title: 'Tab Top',             desc: 'Informal, fits country & transitional decor.' },
    { value: 'flat-panel',          title: 'Flat Panel',          desc: 'Casual and modern. Uses 4-in-1 tape.' },
    { value: 'ripple-fold',         title: 'Ripple Fold',         desc: 'Polished look with clean s-folds.' },
    { value: 'pencil-pleat',        title: 'Pencil Pleat',        desc: 'Traditional, with tightly gathered folds.' }
  ];

  const FULLNESS_BY_HEADER = {
    'grommet':            { options: [1.5, 2.0, 2.5], default: 2.0 },
    'rod-pocket':         { options: [1.5, 2.0, 2.5], default: 2.0 },
    'pinch-pleat-double': { options: [2.0],           default: 2.0 },
    'pinch-pleat-triple': { options: [2.5],           default: 2.5 },
    'back-tab':           { options: [1.5, 2.0, 2.5], default: 2.0 },
    'tab-top':            { options: [1.5, 2.0, 2.5], default: 2.0 },
    'flat-panel':         { options: [1.5, 2.0, 2.5], default: 2.0 },
    'ripple-fold':        { options: [2.0],           default: 2.0 },
    'pencil-pleat':       { options: [2.0],           default: 2.0 }
  };

  const FULLNESS_TAGS = {
    1.0: 'Minimum',
    1.5: 'Budget',
    2.0: 'Recommended',
    2.5: 'Luxe Sheer'
  };

  const FINISHED_LENGTH = [
    { value: 'above_floor', title: '1 Above Floor', desc: 'Easiest to clean — most popular.',     delta: -0.5 },
    { value: 'kiss_floor',  title: 'Kiss Floor',       desc: 'Tailored, exactly grazes the floor.',  delta: 0 },
    { value: 'puddle',      title: '2" Puddle',        desc: 'Dramatic, pools elegantly on the floor.', delta: 2 }
  ];

  const PANEL_TYPES = [
    { value: 'single', title: 'Single Panel', desc: 'One panel, draws to one side.' },
    { value: 'split',  title: 'Split Pair',   desc: 'Two equal panels, open in the middle.' }
  ];

  const ASSET_BASE = (function () {
    const scripts = document.getElementsByTagName('script');
    for (let i = scripts.length - 1; i >= 0; i--) {
      const src = scripts[i].src || '';
      const idx = src.indexOf('measurement-tool.js');
      if (idx !== -1) return src.slice(0, idx);
    }
    return '';
  })();

  function assetUrl(filename) {
    return ASSET_BASE + filename;
  }

  function assetImg(filename, alt) {
    return `<img class="cmt-asset-img" src="${assetUrl(filename)}" alt="${escapeHtml(alt || '')}" loading="lazy">`;
  }

  const HEADER_IMAGES = {
    'grommet': 'curtain_style_hd_1.webp',
    'rod-pocket': 'curtain_style_hd_2.webp',
    'pinch-pleat-double': 'curtain_style_hd_3.webp',
    'pinch-pleat-triple': 'curtain_style_hd_4.webp',
    'back-tab': 'curtain_style_hd_5.webp',
    'tab-top': 'curtain_style_hd_6.webp',
    'flat-panel': 'curtain_style_hd_7.webp',
    'ripple-fold': 'curtain_style_hd_8.webp',
    'pencil-pleat': 'curtain_style_hd_9.webp'
  };

  function panelChoiceVisual(panelType, headerStyle, alt) {
    const headerImage = HEADER_IMAGES[headerStyle] || HEADER_IMAGES.grommet;
    const folder = panelType === 'split' ? 'open' : 'close';
    const src = assetUrl(`${folder}-${headerImage}`);
    const safeAlt = escapeHtml(alt || '');
    const frameStyle = [
      'position:relative',
      'width:100%',
      'height:100%',
      'min-height:190px',
      'overflow:hidden',
      'border-radius:8px',
      'background:#f1ead9'
    ].join(';');
    const imgStyle = [
      'display:block',
      'width:100%',
      'height:100%',
      'object-fit:cover',
      'object-position:center',
      'filter:saturate(1.02)'
    ].join(';');

    return `
      <div style="${frameStyle}" role="img" aria-label="${safeAlt}">
        <img src="${src}" alt="" loading="lazy" style="${imgStyle}">
      </div>
    `;
  }

  const FULLNESS_IMAGES = {
    1.0: 'fullness-1x.webp',
    1.5: 'fullness-2x.webp',
    2.0: 'fullness-2x.webp',
    2.5: 'fullness-25x.webp',
    3.0: 'fullness-3x.webp'
  };

  function fullnessChoiceImage(fullness, panelType, headerStyle) {
    const headerImage = HEADER_IMAGES[headerStyle];
    const panelFolder = panelType === 'split' ? 'split' : 'single';
    if (headerImage && (fullness === 1.5 || fullness === 2.0 || fullness === 2.5)) {
      return `${fullness.toFixed(1)}-${panelFolder}-${headerImage}`;
    }
    return FULLNESS_IMAGES[fullness] || 'fullness-2x.webp';
  }

  const LENGTH_IMAGE_FALLBACKS = {
    above_floor: 'length-float.webp',
    kiss_floor: 'length-kiss-floor.webp',
    puddle: 'length-puddle.webp'
  };

  const KISS_FLOOR_IMAGE_NAMES = {
    'curtain_style_hd_1': 'curtain_style_hd_1-Kiss Floor.webp',
    'curtain_style_hd_2': 'curtain_style_hd_2 - Kiss Floor.webp',
    'curtain_style_hd_3': 'curtain_style_hd_3 - Kiss Floor.webp',
    'curtain_style_hd_4': 'curtain_style_hd_4 - Kiss Floor.webp',
    'curtain_style_hd_5': 'curtain_style_hd_5 - Kiss Floor.webp',
    'curtain_style_hd_6': 'curtain_style_hd_6 - Kiss Floor.webp',
    'curtain_style_hd_7': 'curtain_style_hd_7 -Kiss Floor.webp',
    'curtain_style_hd_8': 'curtain_style_hd_8 -Kiss Floor.webp',
    'curtain_style_hd_9': 'curtain_style_hd_9 -Kiss Floor.webp'
  };

  function lengthChoiceImage(lengthValue, headerStyle) {
    const headerImage = HEADER_IMAGES[headerStyle];
    if (!headerImage) return LENGTH_IMAGE_FALLBACKS[lengthValue] || LENGTH_IMAGE_FALLBACKS.kiss_floor;

    const baseName = headerImage.replace(/\.webp$/i, '');
    if (lengthValue === 'above_floor') return `length-${baseName}-0.5 Above.webp`;
    if (lengthValue === 'kiss_floor') return `length-${KISS_FLOOR_IMAGE_NAMES[baseName] || `${baseName}-Kiss Floor.webp`}`;
    if (lengthValue === 'puddle') return `length-${baseName}-2 Puddle.webp`;
    return LENGTH_IMAGE_FALLBACKS[lengthValue] || LENGTH_IMAGE_FALLBACKS.kiss_floor;
  }

  /* Validation bounds (inches) */
  const BOUNDS = {
    width:  { min: 12, max: 240 },
    height: { min: 12, max: 144 },
    ext:    { min: 0,  max: 24 },
    rise:   { min: 0,  max: 24 }
  };

  /* ----- Step definitions / progress bar ----- */
  const STEP_LABELS = [
    { idx: 1, label: 'Header' },
    { idx: 2, label: 'Rod' },
    { idx: 3, label: 'Width' },
    { idx: 4, label: 'Height' },
    { idx: 5, label: 'Panels' }
  ];

  /* ----- Default state ----- */
  function defaultState() {
    return {
      version: STATE_VERSION,
      currentStep: 1,
      unit: 'inch',
      headerStyle: null,
      hasRod: null,
      flowA: { rodWidth: null, topFrameToFloor: null },
      flowB: { windowWidth: null, extLeft: 8, extRight: 8, rodAboveFrame: 6, windowHeight: null },
      panelType: null,
      fullness: 2.0,
      finishedLength: 'kiss_floor',
      savedAt: Date.now()
    };
  }

  /* ----- Utility ----- */
  const inchToCm = inch => inch * 2.54;
  const cmToInch = cm => cm / 2.54;
  const round1 = n => Math.round(n * 10) / 10;
  const formatNum = n => {
    if (n == null || !isFinite(n)) return '—';
    const r = round1(n);
    return Number.isInteger(r) ? r.toString() : r.toFixed(1);
  };

  function safeParseFloat(v) {
    if (v == null || v === '') return null;
    const n = parseFloat(String(v).replace(/,/g, '.'));
    return isFinite(n) ? n : null;
  }

  /* Persist state to localStorage. Try/catch — fail silently if unavailable. */
  function saveState(state) {
    try {
      const copy = Object.assign({}, state, { savedAt: Date.now() });
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(copy));
    } catch (e) { /* noop */ }
  }
  function loadState() {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || parsed.version !== STATE_VERSION) return null;
      if (parsed.savedAt && Date.now() - parsed.savedAt > TTL_MS) return null;
      return parsed;
    } catch (e) { return null; }
  }
  function clearState() {
    try { window.localStorage.removeItem(STORAGE_KEY); } catch (e) {}
  }

  /* ----- SVG illustrations (inline so the tool is fully self-contained) ----- */
  function svg(content, viewBox = '0 0 200 140') {
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBox}" role="img" aria-hidden="true">${content}</svg>`;
  }

  const PALETTE = {
    wall: '#efe7d8',
    rod: '#3a3a3a',
    fabric: '#f3eadb',
    fabricDark: '#dfd0b3',
    floor: '#e5d7bf',
    window: '#dee9ec',
    accent: '#3d5a6c',
    line: '#b89868',
    dim: '#c8553d'
  };

  /* --- Header style thumbnails --- */
  const HEADER_SVG = {
    grommet: svg(`
      <rect width="200" height="140" fill="${PALETTE.wall}"/>
      <rect x="14" y="22" width="172" height="6" rx="3" fill="${PALETTE.rod}"/>
      ${[28,52,76,100,124,148,172].map(x =>
        `<circle cx="${x}" cy="34" r="5" fill="none" stroke="${PALETTE.rod}" stroke-width="2"/>`
      ).join('')}
      <path d="M22,38 Q34,60 28,128 L36,128 Q34,68 46,40 Q56,60 50,128 L58,128 Q56,70 68,40 Q78,60 72,128 L80,128 Q78,68 92,40 Q102,60 96,128 L104,128 Q102,70 116,40 Q126,60 120,128 L128,128 Q126,68 140,40 Q150,60 144,128 L152,128 Q150,70 164,40 Q174,60 168,128 L178,128 Q176,68 184,40 Z" fill="${PALETTE.fabric}" stroke="${PALETTE.fabricDark}" stroke-width="0.6"/>
    `),
    'rod-pocket': svg(`
      <rect width="200" height="140" fill="${PALETTE.wall}"/>
      <rect x="18" y="22" width="164" height="14" rx="3" fill="${PALETTE.fabric}" stroke="${PALETTE.fabricDark}" stroke-width="0.6"/>
      <path d="M22,36 Q28,60 24,128 L34,128 Q32,72 40,38 Q48,60 44,128 L54,128 Q52,72 60,38 Q68,60 64,128 L74,128 Q72,72 80,38 Q88,60 84,128 L94,128 Q92,72 100,38 Q108,60 104,128 L114,128 Q112,72 120,38 Q128,60 124,128 L134,128 Q132,72 140,38 Q148,60 144,128 L154,128 Q152,72 160,38 Q168,60 164,128 L174,128 Q172,72 180,38 Z" fill="${PALETTE.fabric}" stroke="${PALETTE.fabricDark}" stroke-width="0.6"/>
    `),
    'pinch-pleat-double': svg(`
      <rect width="200" height="140" fill="${PALETTE.wall}"/>
      <rect x="14" y="22" width="172" height="4" fill="${PALETTE.rod}"/>
      ${[24,52,80,108,136,164].map(x => `
        <path d="M${x-6},30 L${x-6},42 M${x},30 L${x},42 M${x+6},30 L${x+6},42" stroke="${PALETTE.fabricDark}" stroke-width="1.5" fill="none"/>
        <circle cx="${x}" cy="34" r="3" fill="${PALETTE.line}"/>`
      ).join('')}
      <path d="M18,42 Q24,60 20,128 L34,128 Q32,68 38,44 Q44,68 40,128 L54,128 Q52,68 60,44 Q68,68 64,128 L78,128 Q76,68 86,44 Q94,68 90,128 L104,128 Q102,68 114,44 Q122,68 118,128 L132,128 Q130,68 142,44 Q150,68 146,128 L160,128 Q158,68 170,44 Q178,68 174,128 L186,128 Z" fill="${PALETTE.fabric}" stroke="${PALETTE.fabricDark}" stroke-width="0.6"/>
    `),
    'pinch-pleat-triple': svg(`
      <rect width="200" height="140" fill="${PALETTE.wall}"/>
      <rect x="14" y="22" width="172" height="4" fill="${PALETTE.rod}"/>
      ${[22,48,74,100,126,152,178].map(x => `
        <path d="M${x-7},30 L${x-7},44 M${x-3.5},30 L${x-3.5},44 M${x},30 L${x},44 M${x+3.5},30 L${x+3.5},44 M${x+7},30 L${x+7},44" stroke="${PALETTE.fabricDark}" stroke-width="1.2" fill="none"/>
        <circle cx="${x}" cy="36" r="3" fill="${PALETTE.line}"/>`
      ).join('')}
      <path d="M16,44 Q22,62 18,128 L32,128 Q30,68 36,46 Q42,68 38,128 L52,128 Q50,68 58,46 Q64,68 60,128 L74,128 Q72,68 84,46 Q92,68 88,128 L102,128 Q100,68 112,46 Q120,68 116,128 L132,128 Q130,68 140,46 Q146,68 142,128 L156,128 Q154,68 166,46 Q174,68 170,128 L184,128 Z" fill="${PALETTE.fabric}" stroke="${PALETTE.fabricDark}" stroke-width="0.6"/>
    `),
    'back-tab': svg(`
      <rect width="200" height="140" fill="${PALETTE.wall}"/>
      <rect x="14" y="22" width="172" height="4" fill="${PALETTE.rod}"/>
      <rect x="20" y="32" width="160" height="10" rx="2" fill="${PALETTE.fabric}" stroke="${PALETTE.fabricDark}" stroke-width="0.6"/>
      <path d="M22,42 Q28,62 24,128 L36,128 Q34,70 42,44 Q50,62 46,128 L58,128 Q56,70 64,44 Q72,62 68,128 L80,128 Q78,70 86,44 Q94,62 90,128 L102,128 Q100,70 108,44 Q116,62 112,128 L124,128 Q122,70 130,44 Q138,62 134,128 L146,128 Q144,70 152,44 Q160,62 156,128 L168,128 Q166,70 174,44 Q180,62 178,128 Z" fill="${PALETTE.fabric}" stroke="${PALETTE.fabricDark}" stroke-width="0.6"/>
    `),
    'tab-top': svg(`
      <rect width="200" height="140" fill="${PALETTE.wall}"/>
      <rect x="14" y="32" width="172" height="4" fill="${PALETTE.rod}"/>
      ${[24,48,72,96,120,144,168].map(x =>
        `<rect x="${x-4}" y="18" width="8" height="20" rx="3" fill="none" stroke="${PALETTE.fabricDark}" stroke-width="1.5"/>`
      ).join('')}
      <path d="M20,40 Q28,60 22,128 L34,128 Q32,68 42,42 Q50,60 46,128 L58,128 Q56,68 66,42 Q74,60 70,128 L82,128 Q80,68 90,42 Q98,60 94,128 L106,128 Q104,68 114,42 Q122,60 118,128 L130,128 Q128,68 138,42 Q146,60 142,128 L154,128 Q152,68 162,42 Q170,60 166,128 L178,128 Q176,68 184,42 Z" fill="${PALETTE.fabric}" stroke="${PALETTE.fabricDark}" stroke-width="0.6"/>
    `),
    'flat-panel': svg(`
      <rect width="200" height="140" fill="${PALETTE.wall}"/>
      <rect x="14" y="22" width="172" height="4" fill="${PALETTE.rod}"/>
      <rect x="18" y="30" width="164" height="98" fill="${PALETTE.fabric}" stroke="${PALETTE.fabricDark}" stroke-width="0.6"/>
      ${[40,60,80,100,120,140,160].map(x => `<line x1="${x}" y1="30" x2="${x}" y2="128" stroke="${PALETTE.fabricDark}" stroke-width="0.5" opacity="0.6"/>`).join('')}
    `),
    'ripple-fold': svg(`
      <rect width="200" height="140" fill="${PALETTE.wall}"/>
      <rect x="14" y="22" width="172" height="3" fill="${PALETTE.rod}"/>
      <path d="M14,30 Q24,40 34,30 Q44,40 54,30 Q64,40 74,30 Q84,40 94,30 Q104,40 114,30 Q124,40 134,30 Q144,40 154,30 Q164,40 174,30 Q184,40 194,30" stroke="${PALETTE.fabricDark}" stroke-width="1.5" fill="none"/>
      <path d="M16,32 Q20,60 16,128 L28,128 Q24,68 30,40 Q38,72 34,128 L46,128 Q42,68 48,40 Q56,72 52,128 L64,128 Q60,68 66,40 Q74,72 70,128 L82,128 Q78,68 84,40 Q92,72 88,128 L100,128 Q96,68 102,40 Q110,72 106,128 L118,128 Q114,68 120,40 Q128,72 124,128 L136,128 Q132,68 138,40 Q146,72 142,128 L154,128 Q150,68 156,40 Q164,72 160,128 L172,128 Q168,68 178,40 Z" fill="${PALETTE.fabric}" stroke="${PALETTE.fabricDark}" stroke-width="0.6"/>
    `),
    'pencil-pleat': svg(`
      <rect width="200" height="140" fill="${PALETTE.wall}"/>
      <rect x="14" y="22" width="172" height="4" fill="${PALETTE.rod}"/>
      <rect x="18" y="30" width="164" height="14" fill="${PALETTE.fabric}" stroke="${PALETTE.fabricDark}" stroke-width="0.6"/>
      ${Array.from({length: 32}).map((_,i)=>`<line x1="${20+i*5}" y1="30" x2="${20+i*5}" y2="44" stroke="${PALETTE.fabricDark}" stroke-width="1" opacity="0.6"/>`).join('')}
      <path d="M20,44 Q26,60 22,128 L32,128 Q30,68 36,46 Q42,62 38,128 L48,128 Q46,68 52,46 Q58,62 54,128 L64,128 Q62,68 68,46 Q74,62 70,128 L80,128 Q78,68 84,46 Q90,62 86,128 L96,128 Q94,68 100,46 Q106,62 102,128 L112,128 Q110,68 116,46 Q122,62 118,128 L128,128 Q126,68 132,46 Q138,62 134,128 L144,128 Q142,68 148,46 Q154,62 150,128 L160,128 Q158,68 164,46 Q170,62 166,128 L176,128 Q174,68 180,46 Z" fill="${PALETTE.fabric}" stroke="${PALETTE.fabricDark}" stroke-width="0.6"/>
    `)
  };

  /* --- Yes/No room visuals --- */
  const YES_NO_SVG = {
    yes: svg(`
      <rect width="240" height="160" fill="${PALETTE.wall}"/>
      <rect y="130" width="240" height="30" fill="${PALETTE.floor}"/>
      <rect x="60" y="34" width="120" height="80" fill="${PALETTE.window}" stroke="#c9c4ba" stroke-width="1"/>
      <line x1="120" y1="34" x2="120" y2="114" stroke="#c9c4ba" stroke-width="1"/>
      <rect x="36" y="22" width="168" height="4" fill="${PALETTE.rod}"/>
      <circle cx="34" cy="24" r="4" fill="${PALETTE.rod}"/>
      <circle cx="206" cy="24" r="4" fill="${PALETTE.rod}"/>
      <path d="M40,26 Q44,80 40,128 L52,128 Q50,82 56,26 Q62,80 58,128 L70,128 Q68,82 74,26 Q80,80 76,128 L88,128 Q86,82 92,26" fill="${PALETTE.fabric}" stroke="${PALETTE.fabricDark}" stroke-width="0.6"/>
      <path d="M148,26 Q152,80 148,128 L160,128 Q158,82 164,26 Q170,80 166,128 L178,128 Q176,82 182,26 Q188,80 184,128 L196,128 Q194,82 200,26" fill="${PALETTE.fabric}" stroke="${PALETTE.fabricDark}" stroke-width="0.6"/>
    `, '0 0 240 160'),
    no: svg(`
      <rect width="240" height="160" fill="${PALETTE.wall}"/>
      <rect y="130" width="240" height="30" fill="${PALETTE.floor}"/>
      <rect x="60" y="34" width="120" height="80" fill="${PALETTE.window}" stroke="#c9c4ba" stroke-width="1"/>
      <line x1="120" y1="34" x2="120" y2="114" stroke="#c9c4ba" stroke-width="1"/>
      <line x1="60" y1="74" x2="180" y2="74" stroke="#c9c4ba" stroke-width="1"/>
      <rect x="58" y="32" width="124" height="84" fill="none" stroke="#9a9381" stroke-width="2"/>
    `, '0 0 240 160')
  };

  /* --- Measurement diagrams --- */
  const MEASURE_SVG = {
    rodWidth: svg(`
      <rect width="280" height="180" fill="${PALETTE.wall}"/>
      <rect y="148" width="280" height="32" fill="${PALETTE.floor}"/>
      <rect x="68" y="46" width="144" height="84" fill="${PALETTE.window}" stroke="#c9c4ba" stroke-width="1"/>
      <line x1="140" y1="46" x2="140" y2="130" stroke="#c9c4ba" stroke-width="1"/>
      <rect x="40" y="34" width="200" height="4" fill="${PALETTE.rod}"/>
      <circle cx="38" cy="36" r="5" fill="${PALETTE.rod}"/>
      <circle cx="242" cy="36" r="5" fill="${PALETTE.rod}"/>
      <path d="M44,38 Q50,80 46,140 L60,140 Q58,86 66,38 Q74,80 70,140 L84,140 Q82,86 90,38" fill="${PALETTE.fabric}" stroke="${PALETTE.fabricDark}" stroke-width="0.5" opacity="0.8"/>
      <path d="M190,38 Q198,86 194,140 L208,140 Q206,86 214,38 Q222,80 218,140 L232,140 Q230,86 236,38" fill="${PALETTE.fabric}" stroke="${PALETTE.fabricDark}" stroke-width="0.5" opacity="0.8"/>
      <line x1="38" y1="20" x2="242" y2="20" stroke="${PALETTE.dim}" stroke-width="1.5"/>
      <line x1="38" y1="16" x2="38" y2="24" stroke="${PALETTE.dim}" stroke-width="1.5"/>
      <line x1="242" y1="16" x2="242" y2="24" stroke="${PALETTE.dim}" stroke-width="1.5"/>
      <rect x="120" y="10" width="40" height="18" rx="9" fill="#fff" stroke="${PALETTE.dim}" stroke-width="1.2"/>
      <text x="140" y="22" text-anchor="middle" font-size="10" font-weight="700" fill="${PALETTE.dim}" font-family="Inter, Arial, sans-serif">Rod W</text>
    `, '0 0 280 180'),
    windowWidth: svg(`
      <rect width="280" height="180" fill="${PALETTE.wall}"/>
      <rect y="148" width="280" height="32" fill="${PALETTE.floor}"/>
      <rect x="60" y="38" width="160" height="100" fill="${PALETTE.window}" stroke="#a9a294" stroke-width="2"/>
      <line x1="140" y1="38" x2="140" y2="138" stroke="#a9a294" stroke-width="1"/>
      <line x1="60" y1="88" x2="220" y2="88" stroke="#a9a294" stroke-width="1"/>
      <line x1="60" y1="20" x2="220" y2="20" stroke="${PALETTE.dim}" stroke-width="1.5"/>
      <line x1="60" y1="16" x2="60" y2="24" stroke="${PALETTE.dim}" stroke-width="1.5"/>
      <line x1="220" y1="16" x2="220" y2="24" stroke="${PALETTE.dim}" stroke-width="1.5"/>
      <rect x="112" y="10" width="56" height="18" rx="9" fill="#fff" stroke="${PALETTE.dim}" stroke-width="1.2"/>
      <text x="140" y="22" text-anchor="middle" font-size="10" font-weight="700" fill="${PALETTE.dim}" font-family="Inter, Arial, sans-serif">Window W</text>
    `, '0 0 280 180'),
    height: svg(`
      <rect width="280" height="220" fill="${PALETTE.wall}"/>
      <rect y="188" width="280" height="32" fill="${PALETTE.floor}"/>
      <rect x="80" y="40" width="120" height="120" fill="${PALETTE.window}" stroke="#a9a294" stroke-width="2"/>
      <line x1="140" y1="40" x2="140" y2="160" stroke="#a9a294" stroke-width="1"/>
      <line x1="240" y1="40" x2="240" y2="188" stroke="${PALETTE.dim}" stroke-width="1.5"/>
      <line x1="236" y1="40" x2="244" y2="40" stroke="${PALETTE.dim}" stroke-width="1.5"/>
      <line x1="236" y1="188" x2="244" y2="188" stroke="${PALETTE.dim}" stroke-width="1.5"/>
      <rect x="220" y="105" width="42" height="18" rx="9" fill="#fff" stroke="${PALETTE.dim}" stroke-width="1.2"/>
      <text x="241" y="117" text-anchor="middle" font-size="10" font-weight="700" fill="${PALETTE.dim}" font-family="Inter, Arial, sans-serif">Height</text>
      <text x="36" y="44" font-size="9" fill="${PALETTE.accent}" font-family="Inter, Arial, sans-serif">Top of frame</text>
      <line x1="60" y1="42" x2="80" y2="42" stroke="${PALETTE.accent}" stroke-width="0.7" stroke-dasharray="2 2"/>
      <text x="36" y="192" font-size="9" fill="${PALETTE.accent}" font-family="Inter, Arial, sans-serif">Floor</text>
      <line x1="60" y1="188" x2="80" y2="188" stroke="${PALETTE.accent}" stroke-width="0.7" stroke-dasharray="2 2"/>
    `, '0 0 280 220'),
    extension: svg(`
      <rect width="320" height="180" fill="${PALETTE.wall}"/>
      <rect y="148" width="320" height="32" fill="${PALETTE.floor}"/>
      <rect x="100" y="48" width="120" height="92" fill="${PALETTE.window}" stroke="#a9a294" stroke-width="2"/>
      <line x1="160" y1="48" x2="160" y2="140" stroke="#a9a294" stroke-width="1"/>
      <rect x="60" y="32" width="200" height="4" fill="${PALETTE.rod}" opacity="0.85"/>
      <circle cx="58" cy="34" r="5" fill="${PALETTE.rod}" opacity="0.85"/>
      <circle cx="262" cy="34" r="5" fill="${PALETTE.rod}" opacity="0.85"/>
      <line x1="60" y1="20" x2="100" y2="20" stroke="${PALETTE.dim}" stroke-width="1.5"/>
      <line x1="60" y1="16" x2="60" y2="24" stroke="${PALETTE.dim}" stroke-width="1.5"/>
      <line x1="100" y1="16" x2="100" y2="24" stroke="${PALETTE.dim}" stroke-width="1.5"/>
      <rect x="64" y="6" width="32" height="14" rx="7" fill="#fff" stroke="${PALETTE.dim}" stroke-width="1"/>
      <text x="80" y="16" text-anchor="middle" font-size="9" font-weight="700" fill="${PALETTE.dim}" font-family="Inter, Arial, sans-serif">A</text>
      <line x1="220" y1="20" x2="260" y2="20" stroke="${PALETTE.dim}" stroke-width="1.5"/>
      <line x1="220" y1="16" x2="220" y2="24" stroke="${PALETTE.dim}" stroke-width="1.5"/>
      <line x1="260" y1="16" x2="260" y2="24" stroke="${PALETTE.dim}" stroke-width="1.5"/>
      <rect x="224" y="6" width="32" height="14" rx="7" fill="#fff" stroke="${PALETTE.dim}" stroke-width="1"/>
      <text x="240" y="16" text-anchor="middle" font-size="9" font-weight="700" fill="${PALETTE.dim}" font-family="Inter, Arial, sans-serif">B</text>
      <line x1="100" y1="170" x2="220" y2="170" stroke="${PALETTE.accent}" stroke-width="1"/>
      <line x1="100" y1="166" x2="100" y2="174" stroke="${PALETTE.accent}" stroke-width="1"/>
      <line x1="220" y1="166" x2="220" y2="174" stroke="${PALETTE.accent}" stroke-width="1"/>
      <text x="160" y="166" text-anchor="middle" font-size="9" fill="${PALETTE.accent}" font-family="Inter, Arial, sans-serif">Window width</text>
    `, '0 0 320 180'),
    rodAbove: svg(`
      <rect width="280" height="220" fill="${PALETTE.wall}"/>
      <rect y="190" width="280" height="30" fill="${PALETTE.floor}"/>
      <rect x="40" y="20" width="200" height="4" fill="${PALETTE.rod}"/>
      <circle cx="38" cy="22" r="5" fill="${PALETTE.rod}"/>
      <circle cx="242" cy="22" r="5" fill="${PALETTE.rod}"/>
      <rect x="80" y="56" width="120" height="118" fill="${PALETTE.window}" stroke="#a9a294" stroke-width="2"/>
      <line x1="140" y1="56" x2="140" y2="174" stroke="#a9a294" stroke-width="1"/>
      <line x1="252" y1="22" x2="252" y2="56" stroke="${PALETTE.dim}" stroke-width="1.5"/>
      <line x1="248" y1="22" x2="256" y2="22" stroke="${PALETTE.dim}" stroke-width="1.5"/>
      <line x1="248" y1="56" x2="256" y2="56" stroke="${PALETTE.dim}" stroke-width="1.5"/>
      <rect x="232" y="30" width="42" height="18" rx="9" fill="#fff" stroke="${PALETTE.dim}" stroke-width="1.2"/>
      <text x="253" y="42" text-anchor="middle" font-size="10" font-weight="700" fill="${PALETTE.dim}" font-family="Inter, Arial, sans-serif">Rise</text>
      <line x1="60" y1="56" x2="60" y2="174" stroke="${PALETTE.accent}" stroke-width="1.5"/>
      <line x1="56" y1="56" x2="64" y2="56" stroke="${PALETTE.accent}" stroke-width="1.5"/>
      <line x1="56" y1="174" x2="64" y2="174" stroke="${PALETTE.accent}" stroke-width="1.5"/>
      <rect x="30" y="105" width="60" height="18" rx="9" fill="#fff" stroke="${PALETTE.accent}" stroke-width="1.2"/>
      <text x="60" y="117" text-anchor="middle" font-size="10" font-weight="700" fill="${PALETTE.accent}" font-family="Inter, Arial, sans-serif">Window H</text>
    `, '0 0 280 220'),
    panels: svg(`
      <rect width="320" height="160" fill="${PALETTE.wall}"/>
      <rect y="130" width="320" height="30" fill="${PALETTE.floor}"/>
      <g>
        <text x="60" y="20" text-anchor="middle" font-size="10" font-weight="600" fill="${PALETTE.accent}" font-family="Inter, Arial, sans-serif">Single Panel</text>
        <rect x="20" y="28" width="80" height="92" fill="${PALETTE.fabric}" stroke="${PALETTE.fabricDark}" stroke-width="0.6"/>
        <rect x="18" y="26" width="84" height="4" fill="${PALETTE.rod}"/>
        ${[28,40,52,64,76,88,100].map(x => `<line x1="${x}" y1="32" x2="${x}" y2="118" stroke="${PALETTE.fabricDark}" stroke-width="0.5" opacity="0.5"/>`).join('')}
      </g>
      <g>
        <text x="240" y="20" text-anchor="middle" font-size="10" font-weight="600" fill="${PALETTE.accent}" font-family="Inter, Arial, sans-serif">Split Pair</text>
        <rect x="178" y="26" width="124" height="4" fill="${PALETTE.rod}"/>
        <rect x="180" y="28" width="50" height="92" fill="${PALETTE.fabric}" stroke="${PALETTE.fabricDark}" stroke-width="0.6"/>
        <rect x="250" y="28" width="50" height="92" fill="${PALETTE.fabric}" stroke="${PALETTE.fabricDark}" stroke-width="0.6"/>
        ${[188,200,212,222].map(x => `<line x1="${x}" y1="32" x2="${x}" y2="118" stroke="${PALETTE.fabricDark}" stroke-width="0.5" opacity="0.5"/>`).join('')}
        ${[258,270,282,294].map(x => `<line x1="${x}" y1="32" x2="${x}" y2="118" stroke="${PALETTE.fabricDark}" stroke-width="0.5" opacity="0.5"/>`).join('')}
      </g>
    `, '0 0 320 160'),
    finishedLength: svg(`
      <rect width="360" height="200" fill="${PALETTE.wall}"/>
      <rect y="156" width="360" height="44" fill="${PALETTE.floor}"/>
      <g>
        <text x="60" y="18" text-anchor="middle" font-size="10" font-weight="600" fill="${PALETTE.accent}" font-family="Inter, Arial, sans-serif">Above Floor</text>
        <rect x="20" y="26" width="80" height="120" fill="${PALETTE.fabric}" stroke="${PALETTE.fabricDark}" stroke-width="0.6"/>
        <line x1="14" y1="156" x2="106" y2="156" stroke="${PALETTE.dim}" stroke-width="1" stroke-dasharray="2 2"/>
      </g>
      <g>
        <text x="180" y="18" text-anchor="middle" font-size="10" font-weight="600" fill="${PALETTE.accent}" font-family="Inter, Arial, sans-serif">Kiss Floor</text>
        <rect x="140" y="26" width="80" height="130" fill="${PALETTE.fabric}" stroke="${PALETTE.fabricDark}" stroke-width="0.6"/>
      </g>
      <g>
        <text x="300" y="18" text-anchor="middle" font-size="10" font-weight="600" fill="${PALETTE.accent}" font-family="Inter, Arial, sans-serif">Puddle</text>
        <rect x="260" y="26" width="80" height="140" fill="${PALETTE.fabric}" stroke="${PALETTE.fabricDark}" stroke-width="0.6"/>
        <path d="M260,166 Q300,178 340,166 L340,176 Q300,184 260,176 Z" fill="${PALETTE.fabric}" stroke="${PALETTE.fabricDark}" stroke-width="0.6"/>
      </g>
    `, '0 0 360 200'),
    fullness: svg(`
      <rect width="320" height="160" fill="${PALETTE.wall}"/>
      <rect y="130" width="320" height="30" fill="${PALETTE.floor}"/>
      <rect x="10" y="22" width="300" height="3" fill="${PALETTE.rod}"/>
      <g>
        <text x="55" y="18" text-anchor="middle" font-size="9" font-weight="600" fill="${PALETTE.accent}" font-family="Inter, Arial, sans-serif">1.5×</text>
        <path d="M14,28 Q22,60 18,128 L34,128 Q32,68 38,30 Q46,60 42,128 L58,128 Q56,68 62,30 Q70,60 66,128 L82,128 Q80,68 86,30 Q94,60 90,128 L96,128 Z" fill="${PALETTE.fabric}" stroke="${PALETTE.fabricDark}" stroke-width="0.6"/>
      </g>
      <g>
        <text x="160" y="18" text-anchor="middle" font-size="9" font-weight="600" fill="${PALETTE.accent}" font-family="Inter, Arial, sans-serif">2.0×</text>
        <path d="M114,28 Q120,58 116,128 L128,128 Q126,66 132,30 Q138,58 134,128 L146,128 Q144,66 150,30 Q156,58 152,128 L164,128 Q162,66 168,30 Q174,58 170,128 L182,128 Q180,66 188,30 Q194,58 190,128 L202,128 Z" fill="${PALETTE.fabric}" stroke="${PALETTE.fabricDark}" stroke-width="0.6"/>
      </g>
      <g>
        <text x="265" y="18" text-anchor="middle" font-size="9" font-weight="600" fill="${PALETTE.accent}" font-family="Inter, Arial, sans-serif">2.5×</text>
        <path d="M220,28 Q224,56 222,128 L232,128 Q230,62 236,30 Q240,56 238,128 L248,128 Q246,62 252,30 Q256,56 254,128 L264,128 Q262,62 268,30 Q272,56 270,128 L280,128 Q278,62 284,30 Q288,56 286,128 L296,128 Q294,62 304,30 Q310,56 306,128 L312,128 Z" fill="${PALETTE.fabric}" stroke="${PALETTE.fabricDark}" stroke-width="0.6"/>
      </g>
    `, '0 0 320 160')
  };

  /* ----- Tool class ----- */
  class MeasurementTool {
    constructor(root, opts) {
      this.root = root;
      this.opts = Object.assign({
        productHandle: 'custom-curtain',
        targetUrl: null,
        title: 'Find Your Perfect Curtain Size',
        subtitle: 'A 5-step guide to your custom curtain dimensions.',
        heroImage: null,
        embedded: false
      }, opts || {});
      this.state = loadState() || defaultState();
      if (!this.state || this.state.version !== STATE_VERSION) {
        this.state = defaultState();
      }
      if (this.state.currentStep === 6 || this.state.currentStep === 7) this.state.currentStep = 5;
      this.state.fullness = 2.0;
      this.state.finishedLength = 'kiss_floor';
      this.render();
    }

    /* ----- Computed helpers ----- */
    isFlowA() { return this.state.hasRod === true; }
    isFlowB() { return this.state.hasRod === false; }

    /* Returns the currently effective step in flow.
       Step indices are 1..7 + "result". */
    canContinue() {
      const s = this.state;
      const step = s.currentStep;
      if (step === 1) return !!s.headerStyle;
      if (step === 2) return s.hasRod === true || s.hasRod === false;
      if (step === 3) {
        if (this.isFlowA()) return this.validNumber(s.flowA.rodWidth, BOUNDS.width);
        if (this.isFlowB()) return (
          this.validNumber(s.flowB.windowWidth, BOUNDS.width) &&
          this.validNumber(s.flowB.extLeft, BOUNDS.ext) &&
          this.validNumber(s.flowB.extRight, BOUNDS.ext)
        );
        return false;
      }
      if (step === 4) {
        if (this.isFlowA()) return this.validNumber(s.flowA.topFrameToFloor, BOUNDS.height);
        if (this.isFlowB()) return (
          this.validNumber(s.flowB.windowHeight, BOUNDS.height) &&
          this.validNumber(s.flowB.rodAboveFrame, BOUNDS.rise)
        );
        return false;
      }
      if (step === 5) return s.panelType === 'single' || s.panelType === 'split';
      return false;
    }

    validNumber(n, bounds) {
      if (n == null || !isFinite(n)) return false;
      const inchVal = this.toInch(n);
      return inchVal >= bounds.min && inchVal <= bounds.max;
    }

    /* Convert displayed value (in current unit) to inches for storage.
       Internal storage is always inches; the input field stores in user's unit
       only at the moment of typing, so we convert on `change`. */
    toInch(v) { return v; /* state values are always inch internally */ }

    /* ----- Calculation ----- */
    compute() {
      const s = this.state;
      const fullness = 2.0;
      const delta = 0; // Kiss Floor

      let rodWidth = 0, baseHeight = 0;
      if (this.isFlowA()) {
        rodWidth = s.flowA.rodWidth || 0;
        baseHeight = s.flowA.topFrameToFloor || 0;
      } else if (this.isFlowB()) {
        rodWidth = (s.flowB.windowWidth || 0) + (s.flowB.extLeft || 0) + (s.flowB.extRight || 0);
        baseHeight = (s.flowB.windowHeight || 0) + (s.flowB.rodAboveFrame || 0);
      }
      const curtainWidth = rodWidth * fullness;
      const curtainHeight = baseHeight + delta;
      const perPanelWidth = s.panelType === 'split' ? curtainWidth / 2 : curtainWidth;
      return {
        rodWidth: round1(rodWidth),
        curtainWidth: round1(curtainWidth),
        curtainHeight: round1(curtainHeight),
        perPanelWidth: round1(perPanelWidth)
      };
    }

    /* ----- Rendering ----- */
    render() {
      const s = this.state;
      const heroBg = this.opts.heroImage ? `style="background-image:url('${this.opts.heroImage}')"` : '';

      this.root.innerHTML = `
        <div class="cmt">
          <header class="cmt-hero" ${heroBg}>
            <div class="cmt-hero__inner">
              <p class="cmt-hero__eyebrow">Curtain Measurement Tool</p>
              <h1 class="cmt-hero__title">${escapeHtml(this.opts.title)}</h1>
              <p class="cmt-hero__subtitle">${escapeHtml(this.opts.subtitle)}</p>
            </div>
          </header>

          <main class="cmt-container">
            ${this.renderProgress()}
            <div class="cmt-toolbar">
              ${this.renderUnitToggle()}
              <button type="button" class="cmt-restart-btn" data-action="restart-confirm">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M2 8a6 6 0 1 0 1.76-4.24M2 2v4h4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
                Start Over
              </button>
              ${this.renderMobileToolbarNav()}
            </div>

            <section class="cmt-stage" aria-live="polite">
              ${this.renderStage()}
            </section>

            <div class="cmt-info">
              <p>Need help with a special window shape? Email <a href="mailto:support@example.com">support@example.com</a>. All curtains are handmade — expect a ±1" tolerance.</p>
            </div>
          </main>

          ${this.renderModal()}
        </div>
      `;

      this.bindEvents();
    }

    renderProgress() {
      const cur = this.state.currentStep;
      const isResult = cur === 'result';
      const currentIdx = isResult ? 6 : cur;
      const stepsHtml = STEP_LABELS.map((s, i) => {
        const done = currentIdx > s.idx;
        const current = currentIdx === s.idx;
        const classes = ['cmt-progress__step'];
        if (done) classes.push('cmt-progress__step--done', 'cmt-progress__step--clickable');
        if (current) classes.push('cmt-progress__step--current');
        const bar = i < STEP_LABELS.length - 1
          ? `<div class="cmt-progress__bar"><div class="cmt-progress__bar-fill"></div></div>`
          : '';
        return `
          <li class="${classes.join(' ')}" data-step="${s.idx}">
            <button type="button" class="cmt-progress__dot" aria-label="Go to step ${s.idx}: ${s.label}">${done ? '' : s.idx}${done ? '<svg width="12" height="12" viewBox="0 0 16 16" fill="white"><path d="M6 11.5L2.5 8l1.2-1.2L6 9.1 12.3 2.8 13.5 4z"/></svg>' : ''}</button>
            <span class="cmt-progress__label">${s.label}</span>
          </li>
          ${bar}
        `;
      }).join('');

      const pct = Math.max(0, Math.min(100, ((isResult ? 5 : Math.max(0, cur - 1)) / 5) * 100));
      return `
        <nav class="cmt-progress" role="navigation" aria-label="Measurement steps">
          <ol class="cmt-progress__steps" role="progressbar" aria-valuemin="1" aria-valuemax="5" aria-valuenow="${isResult ? 5 : cur}">${stepsHtml}</ol>
          <div class="cmt-progress__count">
            <span>Step ${isResult ? 5 : cur} of 5</span>
            <div class="cmt-progress__count-bar"><div class="cmt-progress__count-fill" style="width:${pct}%"></div></div>
          </div>
        </nav>
      `;
    }

    renderUnitToggle() {
      const u = this.state.unit;
      return `
        <div class="cmt-unit-toggle" role="group" aria-label="Unit toggle">
          <button type="button" class="cmt-unit-toggle__btn ${u==='inch'?'cmt-unit-toggle__btn--active':''}" data-action="unit" data-unit="inch" aria-pressed="${u==='inch'}">Inches</button>
          <button type="button" class="cmt-unit-toggle__btn ${u==='cm'?'cmt-unit-toggle__btn--active':''}" data-action="unit" data-unit="cm" aria-pressed="${u==='cm'}">Centimeters</button>
        </div>
      `;
    }

    renderStage() {
      const cur = this.state.currentStep;
      if (cur === 'result') return this.renderResult();
      let body = '';
      switch (cur) {
        case 1: body = this.renderStep1(); break;
        case 2: body = this.renderStep2(); break;
        case 3: body = this.isFlowA() ? this.renderStep3A() : this.renderStep3B(); break;
        case 4: body = this.isFlowA() ? this.renderStep4A() : this.renderStep4B(); break;
        case 5: body = this.renderStep5(); break;
        default: body = this.renderStep1();
      }
      return `${body}${this.renderFooter()}`;
    }

    renderFooter() {
      const canBack = this.state.currentStep > 1;
      const canCont = this.canContinue();
      const isLast = this.state.currentStep === 5;
      return `
        <div class="cmt-footer">
          <button type="button" class="cmt-btn cmt-btn--ghost" data-action="back" ${canBack ? '' : 'style="visibility:hidden"'}>
            <span aria-hidden="true">←</span> Back
          </button>
          <button type="button" class="cmt-btn cmt-btn--primary" data-action="continue" ${canCont ? '' : 'disabled'}>
            ${isLast ? 'See My Result' : 'Continue'} <span class="cmt-btn__arrow" aria-hidden="true">→</span>
          </button>
        </div>
      `;
    }

    renderMobileToolbarNav() {
      if (this.state.currentStep === 'result') return '';
      const canBack = this.state.currentStep > 1;
      const canCont = this.canContinue();
      const isLast = this.state.currentStep === 5;
      return `
        <div class="cmt-mobile-nav">
          <button type="button" class="cmt-btn cmt-btn--ghost" data-action="back" ${canBack ? '' : 'style="visibility:hidden"'}>
            <span aria-hidden="true">←</span> Back
          </button>
          <button type="button" class="cmt-btn cmt-btn--primary" data-action="continue" ${canCont ? '' : 'disabled'}>
            ${isLast ? 'See My Result' : 'Continue'} <span class="cmt-btn__arrow" aria-hidden="true">→</span>
          </button>
        </div>
      `;
    }

    /* ===== STEPS ===== */
    renderStep1() {
      const sel = this.state.headerStyle;
      const cards = HEADER_STYLES.map(h => `
        <button type="button" class="cmt-card ${sel === h.value ? 'cmt-card--selected' : ''}" data-action="select-header" data-value="${h.value}" aria-pressed="${sel === h.value}">
          <div class="cmt-card__visual">${assetImg(HEADER_IMAGES[h.value], h.title)}</div>
          <div class="cmt-card__title">${h.title}</div>
          <p class="cmt-card__desc">${h.desc}</p>
        </button>
      `).join('');
      return `
        <div class="cmt-step">
          <div class="cmt-step__header">
            <p class="cmt-step__eyebrow">Step 1 of 5</p>
            <h2 class="cmt-step__title">Select the Header Style</h2>
            <p class="cmt-step__note">The header determines how your curtain hangs and looks. Pick the style that fits your room.</p>
          </div>
          <div class="cmt-grid">${cards}</div>
        </div>
      `;
    }

    renderStep2() {
      const v = this.state.hasRod;
      const cards = [
        { value: true,  title: 'Yes',  desc: 'I already have a curtain rod or track installed.',     image: 'window_1.webp' },
        { value: false, title: 'No',   desc: 'I haven\'t installed one yet — start from the window.', image: 'window_2.webp' }
      ].map(o => `
        <button type="button" class="cmt-card ${v === o.value ? 'cmt-card--selected' : ''}" data-action="select-rod" data-value="${o.value}" aria-pressed="${v === o.value}">
          <div class="cmt-card__visual">${assetImg(o.image, o.title)}</div>
          <div class="cmt-card__title">${o.title}</div>
          <p class="cmt-card__desc">${o.desc}</p>
        </button>
      `).join('');
      return `
        <div class="cmt-step">
          <div class="cmt-step__header">
            <p class="cmt-step__eyebrow">Step 2 of 5</p>
            <h2 class="cmt-step__title">Do You Have a Drapery Rod Installed Already?</h2>
            <p class="cmt-step__note">This decides what to measure next. Pick "Yes" if your rod is already mounted; pick "No" to measure the window instead.</p>
          </div>
          <div class="cmt-grid cmt-grid--two">${cards}</div>
        </div>
      `;
    }

    renderStep3A() {
      const inchVal = this.state.flowA.rodWidth;
      const dispVal = this.toDisplay(inchVal);
      const errId = 'cmt-err-rodWidth';
      const err = this.fieldErrorText('flowA.rodWidth', inchVal, BOUNDS.width);
      return `
        <div class="cmt-step">
          <div class="cmt-step__header">
            <p class="cmt-step__eyebrow">Step 3 of 5 · You have a rod</p>
            <h2 class="cmt-step__title">What's the total width of your rod?</h2>
            <p class="cmt-step__note">Measure end to end of the rod (finials excluded). For return rods, enter the full U-length.</p>
          </div>
          <div class="cmt-split">
            <div class="cmt-split__visual">${assetImg('width-full-wall.webp', 'Measure rod width')}</div>
            <div class="cmt-split__controls">
              <div class="cmt-field ${err ? 'cmt-field--error' : ''}">
                <label class="cmt-field__label" for="cmt-rodWidth">Rod Width</label>
                <span class="cmt-field__hint">Range: ${this.boundsText(BOUNDS.width)}</span>
                <div class="cmt-input-wrap">
                  <input type="number" inputmode="decimal" step="0.1" class="cmt-input" id="cmt-rodWidth"
                         value="${dispVal == null ? '' : dispVal}" placeholder="e.g. 100"
                         data-action="input-num" data-target="flowA.rodWidth" aria-describedby="${errId}" />
                  <span class="cmt-input-wrap__suffix">${this.unitLabel()}</span>
                </div>
                <p class="cmt-field__error" id="${errId}">${err || ''}</p>
              </div>
            </div>
          </div>
        </div>
      `;
    }

    renderStep3B() {
      const fb = this.state.flowB;
      const wErr = this.fieldErrorText('flowB.windowWidth', fb.windowWidth, BOUNDS.width);
      const lErr = this.fieldErrorText('flowB.extLeft', fb.extLeft, BOUNDS.ext);
      const rErr = this.fieldErrorText('flowB.extRight', fb.extRight, BOUNDS.ext);
      return `
        <div class="cmt-step">
          <div class="cmt-step__header">
            <p class="cmt-step__eyebrow">Step 3 of 5 · No rod yet</p>
            <h2 class="cmt-step__title">Measure your window and rod extension</h2>
            <p class="cmt-step__note">For maximum window exposure, let the rod extend 6–10 inches on each side. Default is 8 inches per side.</p>
          </div>
          <div class="cmt-split">
            <div class="cmt-split__visual">${assetImg('width-rod-extend.webp', 'Measure window width and rod extension')}</div>
            <div class="cmt-split__controls">
              <div class="cmt-field ${wErr ? 'cmt-field--error' : ''}">
                <label class="cmt-field__label" for="cmt-winW">Window Width</label>
                <span class="cmt-field__hint">Frame outer edge to edge. Range: ${this.boundsText(BOUNDS.width)}</span>
                <div class="cmt-input-wrap">
                  <input type="number" inputmode="decimal" step="0.1" class="cmt-input" id="cmt-winW"
                         value="${this.toDisplay(fb.windowWidth) ?? ''}" placeholder="e.g. 48"
                         data-action="input-num" data-target="flowB.windowWidth" />
                  <span class="cmt-input-wrap__suffix">${this.unitLabel()}</span>
                </div>
                <p class="cmt-field__error">${wErr || ''}</p>
              </div>
              <div class="cmt-input-row">
                <div class="cmt-field ${lErr ? 'cmt-field--error' : ''}">
                  <label class="cmt-field__label" for="cmt-extL">Left Extension (A)</label>
                  <span class="cmt-field__hint">Default 8 in</span>
                  <div class="cmt-input-wrap">
                    <input type="number" inputmode="decimal" step="0.1" class="cmt-input" id="cmt-extL"
                           value="${this.toDisplay(fb.extLeft) ?? ''}" data-action="input-num" data-target="flowB.extLeft" />
                    <span class="cmt-input-wrap__suffix">${this.unitLabel()}</span>
                  </div>
                  <p class="cmt-field__error">${lErr || ''}</p>
                </div>
                <div class="cmt-field ${rErr ? 'cmt-field--error' : ''}">
                  <label class="cmt-field__label" for="cmt-extR">Right Extension (B)</label>
                  <span class="cmt-field__hint">Default 8 in</span>
                  <div class="cmt-input-wrap">
                    <input type="number" inputmode="decimal" step="0.1" class="cmt-input" id="cmt-extR"
                           value="${this.toDisplay(fb.extRight) ?? ''}" data-action="input-num" data-target="flowB.extRight" />
                    <span class="cmt-input-wrap__suffix">${this.unitLabel()}</span>
                  </div>
                  <p class="cmt-field__error">${rErr || ''}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      `;
    }

    renderStep4A() {
      const v = this.state.flowA.topFrameToFloor;
      const err = this.fieldErrorText('flowA.topFrameToFloor', v, BOUNDS.height);
      return `
        <div class="cmt-step">
          <div class="cmt-step__header">
            <p class="cmt-step__eyebrow">Step 4 of 5 · You have a rod</p>
            <h2 class="cmt-step__title">What's the height from top frame to floor?</h2>
            <p class="cmt-step__note">Measure from the very top of the window frame straight down to the floor. Check both sides — use the smaller value if they differ.</p>
          </div>
          <div class="cmt-split">
            <div class="cmt-split__visual">${assetImg('height-floor.webp', 'Measure height to floor')}</div>
            <div class="cmt-split__controls">
              <div class="cmt-field ${err ? 'cmt-field--error' : ''}">
                <label class="cmt-field__label" for="cmt-h">Top Frame to Floor</label>
                <span class="cmt-field__hint">Range: ${this.boundsText(BOUNDS.height)}</span>
                <div class="cmt-input-wrap">
                  <input type="number" inputmode="decimal" step="0.1" class="cmt-input" id="cmt-h"
                         value="${this.toDisplay(v) ?? ''}" placeholder="e.g. 96"
                         data-action="input-num" data-target="flowA.topFrameToFloor" />
                  <span class="cmt-input-wrap__suffix">${this.unitLabel()}</span>
                </div>
                <p class="cmt-field__error">${err || ''}</p>
              </div>
            </div>
          </div>
        </div>
      `;
    }

    renderStep4B() {
      const fb = this.state.flowB;
      const wErr = this.fieldErrorText('flowB.windowHeight', fb.windowHeight, BOUNDS.height);
      const rErr = this.fieldErrorText('flowB.rodAboveFrame', fb.rodAboveFrame, BOUNDS.rise);
      return `
        <div class="cmt-step">
          <div class="cmt-step__header">
            <p class="cmt-step__eyebrow">Step 4 of 5 · No rod yet</p>
            <h2 class="cmt-step__title">Top frame to floor & rod placement</h2>
            <p class="cmt-step__note">We recommend mounting the rod as high as possible — usually 2–3 inches below the ceiling. Default rise is 6 inches above the frame.</p>
          </div>
          <div class="cmt-split">
            <div class="cmt-split__visual">${assetImg('height-ceiling.webp', 'Measure rod height above frame')}</div>
            <div class="cmt-split__controls">
              <div class="cmt-field ${wErr ? 'cmt-field--error' : ''}">
                <label class="cmt-field__label" for="cmt-winH">Top Frame to Floor</label>
                <span class="cmt-field__hint">Frame top to frame bottom. Range: ${this.boundsText(BOUNDS.height)}</span>
                <div class="cmt-input-wrap">
                  <input type="number" inputmode="decimal" step="0.1" class="cmt-input" id="cmt-winH"
                         value="${this.toDisplay(fb.windowHeight) ?? ''}" placeholder="e.g. 60"
                         data-action="input-num" data-target="flowB.windowHeight" />
                  <span class="cmt-input-wrap__suffix">${this.unitLabel()}</span>
                </div>
                <p class="cmt-field__error">${wErr || ''}</p>
              </div>
              <div class="cmt-field ${rErr ? 'cmt-field--error' : ''}">
                <label class="cmt-field__label" for="cmt-rise">Rod Above Frame</label>
                <span class="cmt-field__hint">Default 6 in. Range: 0–24 in</span>
                <div class="cmt-input-wrap">
                  <input type="number" inputmode="decimal" step="0.1" class="cmt-input" id="cmt-rise"
                         value="${this.toDisplay(fb.rodAboveFrame) ?? ''}" data-action="input-num" data-target="flowB.rodAboveFrame" />
                  <span class="cmt-input-wrap__suffix">${this.unitLabel()}</span>
                </div>
                <p class="cmt-field__error">${rErr || ''}</p>
              </div>
            </div>
          </div>
        </div>
      `;
    }

    renderStep5() {
      const sel = this.state.panelType;
      const header = this.state.headerStyle;
      const headerTitle = (HEADER_STYLES.find(h => h.value === header) || {}).title || '';
      const cards = PANEL_TYPES.map(p => `
        <button type="button" class="cmt-card ${sel === p.value ? 'cmt-card--selected' : ''}" data-action="select-panel" data-value="${p.value}" aria-pressed="${sel === p.value}">
          <div class="cmt-card__visual">${panelChoiceVisual(p.value, header, `${headerTitle ? headerTitle + ' - ' : ''}${p.title}`)}</div>
          <div class="cmt-card__title">${p.title}</div>
          <p class="cmt-card__desc">${p.desc}</p>
        </button>
      `).join('');
      return `
        <div class="cmt-step">
          <div class="cmt-step__header">
            <p class="cmt-step__eyebrow">Step 5 of 5</p>
            <h2 class="cmt-step__title">Do you want Single or Split Panels?</h2>
            <p class="cmt-step__note">Single Panel draws to one side. Split Pair is two equal panels meeting in the middle.</p>
          </div>
          <div class="cmt-grid cmt-grid--two">${cards}</div>
        </div>
      `;
    }

    renderStep6() {
      const header = this.state.headerStyle;
      const cfg = FULLNESS_BY_HEADER[header] || { options: [1.5, 2.0, 2.5], default: 2.0 };
      const sel = this.state.fullness;
      const panel = this.state.panelType;
      const cards = cfg.options.map(opt => `
        <button type="button" class="cmt-card cmt-fullness-card ${sel === opt ? 'cmt-card--selected' : ''}" data-action="select-fullness" data-value="${opt}" aria-pressed="${sel === opt}">
          <div class="cmt-card__visual cmt-fullness-card__visual">${assetImg(fullnessChoiceImage(opt, panel, header), `${opt.toFixed(1)}x Fullness`)}</div>
          <div class="cmt-card__title">${opt.toFixed(1)}× Fullness</div>
          <p class="cmt-card__desc">${FULLNESS_TAGS[opt] || 'Choice'}</p>
        </button>
      `).join('');
      const banner = cfg.note ? `<div class="cmt-banner">${cfg.note}</div>` : '';
      return `
        <div class="cmt-step">
          <div class="cmt-step__header">
            <p class="cmt-step__eyebrow">Step 6 of 7</p>
            <h2 class="cmt-step__title">Choose Fullness</h2>
            <p class="cmt-step__note">The ratio between fabric width and the space you want to cover. Larger fullness = richer drape.</p>
          </div>
          ${banner}
          <div class="cmt-grid cmt-fullness-grid ${cfg.options.length === 1 ? 'cmt-grid--single' : (cfg.options.length >= 3 ? 'cmt-grid--three' : 'cmt-grid--two')}">${cards}</div>
        </div>
      `;
    }

    miniFullnessSvg(opt) {
      const folds = Math.max(3, Math.round(opt * 4));
      const w = 200;
      const spacing = w / folds;
      let path = `M${0},20 `;
      for (let i = 0; i < folds; i++) {
        const x = i * spacing + spacing * 0.3;
        path += `Q${i * spacing + spacing * 0.5},35 ${(i + 1) * spacing},20 `;
      }
      let panels = '';
      for (let i = 0; i < folds; i++) {
        const x = i * spacing + spacing * 0.5;
        panels += `<path d="M${i*spacing+2},22 Q${x},48 ${x-2},108 L${x+spacing*0.4},108 Q${x+spacing*0.6},48 ${(i+1)*spacing-2},22 Z" fill="${PALETTE.fabric}" stroke="${PALETTE.fabricDark}" stroke-width="0.5"/>`;
      }
      return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 120" aria-hidden="true"><rect width="200" height="120" fill="${PALETTE.wall}"/><rect y="108" width="200" height="12" fill="${PALETTE.floor}"/><rect x="0" y="16" width="200" height="3" fill="${PALETTE.rod}"/>${panels}</svg>`;
    }

    renderStep7() {
      const sel = this.state.finishedLength;
      const header = this.state.headerStyle;
      const cards = FINISHED_LENGTH.map(f => `
        <button type="button" class="cmt-card ${sel === f.value ? 'cmt-card--selected' : ''}" data-action="select-length" data-value="${f.value}" aria-pressed="${sel === f.value}">
          <div class="cmt-card__visual" style="aspect-ratio: 1.2 / 1;">${assetImg(lengthChoiceImage(f.value, header), f.title)}</div>
          <div class="cmt-card__title">${f.title}</div>
          <p class="cmt-card__desc">${f.desc}</p>
        </button>
      `).join('');
      return `
        <div class="cmt-step">
          <div class="cmt-step__header">
            <p class="cmt-step__eyebrow">Step 7 of 7</p>
            <h2 class="cmt-step__title">Pick your Finished Length</h2>
            <p class="cmt-step__note">Choose how the bottom hem meets the floor.</p>
          </div>
          <div class="cmt-grid cmt-grid--three">${cards}</div>
        </div>
      `;
    }

    miniFinishSvg(kind) {
      const fabricBottomY = kind === 'above_floor' ? 100 : (kind === 'kiss_floor' ? 108 : 114);
      const puddle = kind === 'puddle' ? `<path d="M30,118 Q100,128 170,118 L170,126 Q100,132 30,126 Z" fill="${PALETTE.fabric}" stroke="${PALETTE.fabricDark}" stroke-width="0.5"/>` : '';
      const gapLine = kind === 'above_floor' ? `<line x1="30" y1="110" x2="170" y2="110" stroke="${PALETTE.dim}" stroke-width="0.8" stroke-dasharray="3 3"/>` : '';
      return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 130" aria-hidden="true">
        <rect width="200" height="130" fill="${PALETTE.wall}"/>
        <rect y="110" width="200" height="20" fill="${PALETTE.floor}"/>
        <rect x="20" y="14" width="160" height="3" fill="${PALETTE.rod}"/>
        <rect x="30" y="18" width="140" height="${fabricBottomY-18}" fill="${PALETTE.fabric}" stroke="${PALETTE.fabricDark}" stroke-width="0.6"/>
        ${[50,70,90,110,130,150].map(x=>`<line x1="${x}" y1="20" x2="${x}" y2="${fabricBottomY}" stroke="${PALETTE.fabricDark}" stroke-width="0.4" opacity="0.4"/>`).join('')}
        ${puddle}${gapLine}
      </svg>`;
    }

    renderResult() {
      const s = this.state;
      s.fullness = 2.0;
      s.finishedLength = 'kiss_floor';
      const c = this.compute();
      const headerLabel = (HEADER_STYLES.find(h => h.value === s.headerStyle) || {}).title || '—';
      const panelLabel = (PANEL_TYPES.find(p => p.value === s.panelType) || {}).title || '—';
      const lengthLabel = (FINISHED_LENGTH.find(f => f.value === s.finishedLength) || {}).title || '—';

      const wInch = c.curtainWidth;
      const hInch = c.curtainHeight;
      const wCm = round1(inchToCm(wInch));
      const hCm = round1(inchToCm(hInch));

      const isInch = s.unit === 'inch';
      const wPrimary = isInch ? `${formatNum(wInch)}<span class="cmt-result__dim-unit">in</span>` : `${formatNum(wCm)}<span class="cmt-result__dim-unit">cm</span>`;
      const wSecondary = isInch ? `${formatNum(wCm)} cm` : `${formatNum(wInch)} in`;
      const hPrimary = isInch ? `${formatNum(hInch)}<span class="cmt-result__dim-unit">in</span>` : `${formatNum(hCm)}<span class="cmt-result__dim-unit">cm</span>`;
      const hSecondary = isInch ? `${formatNum(hCm)} cm` : `${formatNum(hInch)} in`;

      const perPanelInch = c.perPanelWidth;
      const perPanelDisplay = s.panelType === 'split'
        ? `${formatNum(isInch ? perPanelInch : inchToCm(perPanelInch))} ${isInch ? 'in' : 'cm'} × 2`
        : `${formatNum(isInch ? perPanelInch : inchToCm(perPanelInch))} ${isInch ? 'in' : 'cm'}`;

      const shopUrl = this.buildShopUrl(c);

      return `
        <div class="cmt-result">
          <div class="cmt-result__header">
            <p class="cmt-result__eyebrow">All set</p>
            <h2 class="cmt-result__title">Your Recommended Curtain Size</h2>
            <p class="cmt-result__subtitle">Based on your measurements and choices.</p>
          </div>

          <div class="cmt-result__card">
            <div class="cmt-result__dims">
              <div class="cmt-result__dim">
                <div class="cmt-result__dim-label">Total Width</div>
                <div class="cmt-result__dim-value">${wPrimary}</div>
                <div class="cmt-result__dim-alt">${wSecondary}</div>
              </div>
              <div class="cmt-result__dim">
                <div class="cmt-result__dim-label">Total Height</div>
                <div class="cmt-result__dim-value">${hPrimary}</div>
                <div class="cmt-result__dim-alt">${hSecondary}</div>
              </div>
            </div>

            <div class="cmt-result__specs">
              <div class="cmt-result__spec"><span class="cmt-result__spec-label">Header Style</span><span class="cmt-result__spec-value">${headerLabel}</span></div>
              <div class="cmt-result__spec"><span class="cmt-result__spec-label">Panel Type</span><span class="cmt-result__spec-value">${panelLabel}</span></div>
              <div class="cmt-result__spec"><span class="cmt-result__spec-label">Fullness</span><span class="cmt-result__spec-value">${s.fullness ? s.fullness.toFixed(1) + '×' : '—'}</span></div>
              <div class="cmt-result__spec"><span class="cmt-result__spec-label">Finished Length</span><span class="cmt-result__spec-value">${lengthLabel}</span></div>
              <div class="cmt-result__spec"><span class="cmt-result__spec-label">Per-Panel Width</span><span class="cmt-result__spec-value">${perPanelDisplay}</span></div>
              <div class="cmt-result__spec"><span class="cmt-result__spec-label">Source</span><span class="cmt-result__spec-value">${this.isFlowA() ? 'From your rod' : 'From your window'}</span></div>
            </div>
          </div>

          <div class="cmt-result__actions">
            <a class="cmt-btn cmt-btn--primary" href="${shopUrl}" data-action="shop">
              Shop This Curtain <span class="cmt-btn__arrow" aria-hidden="true">→</span>
            </a>
            <button type="button" class="cmt-btn cmt-btn--ghost" data-action="restart-confirm">Start Over</button>
          </div>

          <p class="cmt-result__disclaimer">All curtains are handmade — a dimensional tolerance of ±1" is expected.</p>
        </div>
      `;
    }

    renderModal() {
      return `
        <div class="cmt-modal" id="cmt-modal" role="dialog" aria-modal="true" aria-labelledby="cmt-modal-title">
          <div class="cmt-modal__box">
            <h3 class="cmt-modal__title" id="cmt-modal-title">Start over?</h3>
            <p class="cmt-modal__body">This will clear all your selections and return to Step 1.</p>
            <div class="cmt-modal__actions">
              <button type="button" class="cmt-btn cmt-btn--ghost" data-action="modal-cancel">Cancel</button>
              <button type="button" class="cmt-btn cmt-btn--primary" data-action="restart-confirmed">Yes, start over</button>
            </div>
          </div>
        </div>
      `;
    }

    buildShopUrl(c) {
      const s = this.state;
      const base = '/collections/linen-curtains';
      const params = new URLSearchParams({
        width: formatNum(c.curtainWidth),
        height: formatNum(c.curtainHeight),
        header: s.headerStyle || '',
        panel: s.panelType || '',
        fullness: '2.0',
        finished: 'kiss_floor',
        unit: s.unit,
        source: 'measurement-tool',
        v: String(STATE_VERSION)
      });
      return base + '?' + params.toString();
    }

    saveShopSelection() {
      const s = this.state;
      const valueOrEmpty = value => value == null ? '' : String(value);
      const displayMeasure = inchValue => inchValue == null ? '' : valueOrEmpty(this.toDisplay(inchValue));
      const installed = s.hasRod === true ? 'Yes' : (s.hasRod === false ? 'No' : '');
      const payload = {
        headerStyle: valueOrEmpty(s.headerStyle),
        installed,
        rodWidth: displayMeasure(s.flowA && s.flowA.rodWidth),
        windowWidth: displayMeasure(s.flowB && s.flowB.windowWidth),
        leftExtensionA: displayMeasure(s.flowB && s.flowB.extLeft),
        rightExtensionB: displayMeasure(s.flowB && s.flowB.extRight),
        topFrameToFloor: displayMeasure(s.flowA && s.flowA.topFrameToFloor),
        rodAboveFrame: displayMeasure(s.flowB && s.flowB.rodAboveFrame),
        panels: valueOrEmpty(s.panelType),
        fullness: '2.0',
        finishedLength: 'kiss_floor',
        unit: valueOrEmpty(s.unit),
        source: 'measurement-tool',
        savedAt: new Date().toISOString()
      };

      try {
        window.localStorage.setItem(SHOP_SELECTION_STORAGE_KEY, JSON.stringify(payload));
      } catch (e) { /* noop */ }
    }

    /* ===== Helpers ===== */
    unitLabel() { return this.state.unit === 'inch' ? 'in' : 'cm'; }

    /* Convert internal inch -> displayed value in current unit */
    toDisplay(inchVal) {
      if (inchVal == null) return null;
      const v = this.state.unit === 'inch' ? inchVal : inchToCm(inchVal);
      return formatNum(v);
    }

    /* Convert input (in current unit) to inches for storage */
    fromInput(v) {
      const n = safeParseFloat(v);
      if (n == null) return null;
      return this.state.unit === 'inch' ? n : cmToInch(n);
    }

    boundsText(b) {
      if (this.state.unit === 'inch') return `${b.min}–${b.max} in`;
      return `${formatNum(inchToCm(b.min))}–${formatNum(inchToCm(b.max))} cm`;
    }

    fieldErrorText(path, inchVal, bounds) {
      if (inchVal == null) return null;
      if (!isFinite(inchVal)) return 'Please enter a valid number.';
      if (inchVal < bounds.min) return `Must be at least ${this.state.unit === 'inch' ? bounds.min + ' in' : formatNum(inchToCm(bounds.min)) + ' cm'}.`;
      if (inchVal > bounds.max) return `Must be at most ${this.state.unit === 'inch' ? bounds.max + ' in' : formatNum(inchToCm(bounds.max)) + ' cm'}.`;
      return null;
    }

    /* ===== Event binding ===== */
    bindEvents() {
      const root = this.root;
      root.querySelectorAll('[data-action]').forEach(el => {
        const action = el.dataset.action;
        if (action === 'unit') {
          el.addEventListener('click', () => this.setUnit(el.dataset.unit));
        } else if (action === 'select-header') {
          el.addEventListener('click', () => this.selectHeader(el.dataset.value));
        } else if (action === 'select-rod') {
          el.addEventListener('click', () => this.selectRod(el.dataset.value === 'true'));
        } else if (action === 'select-panel') {
          el.addEventListener('click', () => this.selectPanel(el.dataset.value));
        } else if (action === 'select-fullness') {
          el.addEventListener('click', () => this.selectFullness(parseFloat(el.dataset.value)));
        } else if (action === 'select-length') {
          el.addEventListener('click', () => this.selectLength(el.dataset.value));
        } else if (action === 'continue') {
          el.addEventListener('click', () => this.goNext());
        } else if (action === 'back') {
          el.addEventListener('click', () => this.goBack());
        } else if (action === 'restart-confirm') {
          el.addEventListener('click', () => this.openModal());
        } else if (action === 'restart-confirmed') {
          el.addEventListener('click', () => this.restart());
        } else if (action === 'modal-cancel') {
          el.addEventListener('click', () => this.closeModal());
        } else if (action === 'shop') {
          el.addEventListener('click', () => this.saveShopSelection());
        }
      });

      /* Number inputs: live update without destroying DOM mid-click.
         Re-rendering on blur would replace the Continue button between
         mousedown/mouseup and the click would be lost. Instead we update
         state + disabled state + inline error live, no full re-render. */
      root.querySelectorAll('input[data-action="input-num"]').forEach(input => {
        input.addEventListener('input', e => {
          this.updateNumber(input.dataset.target, input.value, /*re-render*/ false);
          this.updateInlineError(input);
        });
        input.addEventListener('blur', () => this.updateInlineError(input));
      });

      /* Progress step click (only for done steps) */
      root.querySelectorAll('.cmt-progress__step--clickable').forEach(li => {
        li.addEventListener('click', () => {
          const idx = parseInt(li.dataset.step, 10);
          if (!isNaN(idx) && idx >= 1 && idx <= 5) this.goToStep(idx);
        });
      });
    }

    /* ===== State mutations ===== */
    setUnit(unit) {
      if (unit !== 'inch' && unit !== 'cm') return;
      if (this.state.unit === unit) return;
      this.state.unit = unit;
      this.persistAndRender();
    }

    selectHeader(v) {
      this.state.headerStyle = v;
      this.state.fullness = 2.0;
      this.state.finishedLength = 'kiss_floor';
      if (this.state.currentStep === 1) this.state.currentStep = 2;
      this.persistAndRender();
      this.scrollToTop();
    }

    selectRod(v) {
      this.state.hasRod = v;
      if (this.state.currentStep === 2) this.state.currentStep = 3;
      this.persistAndRender();
      this.scrollToTop();
    }

    selectPanel(v) {
      this.state.panelType = v;
      this.persistAndRender();
    }

    selectFullness(v) {
      this.state.fullness = 2.0;
      this.persistAndRender();
    }

    selectLength(v) {
      this.state.finishedLength = 'kiss_floor';
      this.persistAndRender();
    }

    /* Update a nested numeric field. `target` is dot-path like "flowA.rodWidth". */
    updateNumber(target, rawValue, doRender) {
      const inchVal = this.fromInput(rawValue);
      const parts = target.split('.');
      let obj = this.state;
      for (let i = 0; i < parts.length - 1; i++) obj = obj[parts[i]];
      obj[parts[parts.length - 1]] = inchVal;
      saveState(this.state);
      if (doRender) this.render();
      else this.updateFooterDisabledState();
    }

    updateFooterDisabledState() {
      this.root.querySelectorAll('[data-action="continue"]').forEach(btn => {
        btn.disabled = !this.canContinue();
      });
    }

    /* Update inline validation error for an input without re-rendering */
    updateInlineError(input) {
      const target = input.dataset.target;
      const parts = target.split('.');
      let obj = this.state;
      for (let i = 0; i < parts.length - 1; i++) obj = obj[parts[i]];
      const inchVal = obj[parts[parts.length - 1]];
      const bounds = this.boundsForTarget(target);
      const errText = this.fieldErrorText(target, inchVal, bounds);
      const field = input.closest('.cmt-field');
      if (!field) return;
      if (errText) field.classList.add('cmt-field--error');
      else field.classList.remove('cmt-field--error');
      const errEl = field.querySelector('.cmt-field__error');
      if (errEl) errEl.textContent = errText || '';
    }

    boundsForTarget(target) {
      if (target === 'flowA.rodWidth' || target === 'flowB.windowWidth') return BOUNDS.width;
      if (target === 'flowA.topFrameToFloor' || target === 'flowB.windowHeight') return BOUNDS.height;
      if (target === 'flowB.extLeft' || target === 'flowB.extRight') return BOUNDS.ext;
      if (target === 'flowB.rodAboveFrame') return BOUNDS.rise;
      return { min: 0, max: 1e9 };
    }

    goNext() {
      if (!this.canContinue()) return;
      if (this.state.currentStep === 5) {
        this.state.fullness = 2.0;
        this.state.finishedLength = 'kiss_floor';
        this.state.currentStep = 'result';
      } else {
        this.state.currentStep += 1;
      }
      this.persistAndRender();
      this.scrollToTop();
    }

    goBack() {
      if (this.state.currentStep === 'result') {
        this.state.currentStep = 5;
      } else if (this.state.currentStep > 1) {
        this.state.currentStep -= 1;
      }
      this.persistAndRender();
      this.scrollToTop();
    }

    goToStep(idx) {
      if (this.state.currentStep === idx) return;
      this.state.currentStep = idx;
      this.persistAndRender();
      this.scrollToTop();
    }

    openModal() {
      const m = this.root.querySelector('#cmt-modal');
      if (m) m.classList.add('cmt-modal--open');
    }
    closeModal() {
      const m = this.root.querySelector('#cmt-modal');
      if (m) m.classList.remove('cmt-modal--open');
    }

    restart() {
      clearState();
      this.state = defaultState();
      this.render();
      this.scrollToTop();
    }

    persistAndRender() { saveState(this.state); this.render(); }

    scrollToTop() {
      try {
        const target = this.root.querySelector('.cmt-stage');
        if (target && typeof target.scrollIntoView === 'function') {
          const top = target.getBoundingClientRect().top + window.scrollY - 24;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      } catch (e) {}
    }
  }

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /* ----- Expose & auto-init ----- */
  window.CurtainMeasurementTool = MeasurementTool;

  function autoInit() {
    document.querySelectorAll('[data-cmt-root]').forEach(el => {
      if (el.dataset.cmtInitialized) return;
      el.dataset.cmtInitialized = '1';
      const opts = {
        productHandle: el.dataset.productHandle || 'custom-curtain',
        targetUrl: el.dataset.targetUrl || null,
        title: el.dataset.title || 'Find Your Perfect Curtain Size',
        subtitle: el.dataset.subtitle || 'A 5-step guide to your custom curtain dimensions.',
        heroImage: el.dataset.heroImage || null,
        embedded: el.dataset.embedded === '1'
      };
      new MeasurementTool(el, opts);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoInit);
  } else {
    autoInit();
  }
})();
